import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import {
  CreateCustomerInput,
  DataService,
  Dialog,
  GetCustomerListQuery,
  SharedModule,
} from "@bb-vendure/admin-ui/core";
import { CustomerGroup } from "@vendure/core";
import { concat, Observable, of, Subject } from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  tap,
} from "rxjs/operators";
import { GetCustomersForCouponQuery } from "../../generated-gql/graphql";
import { CustomerFilterTableComponent } from "./customer-filter-table/customer-filter-table.component";

export type Customer = GetCustomerListQuery["customers"]["items"][number];

export type SelectCustomerDGroupialogResult = {
  selectedCustomers?: {
    id: string;
    firstName: string;
    lastName: string;
  }[];
};

@Component({
  imports: [SharedModule, CustomerFilterTableComponent],
  standalone: true,
  templateUrl: "./select-customer-filter-dialog.component.html",
  styleUrls: ["./select-customer-filter-dialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectCustomerFilterDialogComponent
  implements OnInit, Dialog<SelectCustomerDGroupialogResult>
{
  resolveWith: (result?: SelectCustomerDGroupialogResult) => void;

  title: string = "Select customer";

  @ViewChild(CustomerFilterTableComponent)
  customerTable!: CustomerFilterTableComponent;

  customers$: Observable<Customer[]>;
  isLoading = false;
  input$ = new Subject<string>();
  selectedCustomer: Customer[] = [];

  constructor(
    private dataService: DataService,
    private formBuilder: UntypedFormBuilder
  ) {}

  ngOnInit(): void {
    this.customers$ = concat(
      this.dataService.customer
        .getCustomerList(10, 0)
        .mapStream(({ customers }) => customers.items), // default items
      this.input$.pipe(
        debounceTime(200),
        distinctUntilChanged(),
        tap(() => (this.isLoading = true)),
        switchMap((term) =>
          this.dataService.customer
            .getCustomerList(10, 0, term)
            .mapStream(({ customers }) => customers.items)
            .pipe(
              catchError(() => of([])), // empty list on error
              tap(() => (this.isLoading = false))
            )
        )
      )
    );
  }

  trackByFn(item: Customer) {
    return item.id;
  }

  cancel() {
    this.resolveWith();
  }

  select() {
    const selectedCustomers = this.customerTable.selected;

    this.resolveWith({
      selectedCustomers: selectedCustomers,
    });
  }
}
